/**
 * The shared substrate (plan §4.2, test-plan §1a `BoardAssembly`): every family emits this,
 * and the cut list, pack, elevation, viewer and validation read only it.
 *
 * Units: `finished`, `cut`, `thicknessTicks`, `glueUp.stripWidthTicks` are 0.1 mm ticks;
 * `placement`, `bounds`, `envelope`, `datumMm`, `datums[].mm` are millimetres.
 * Coordinates: origin floor / outer left / back face of the piece; +x right, +y up, +z front.
 */
import { z } from 'zod';
import { ContractSchema } from './contract';

const hex64 = z.string().regex(/^[0-9a-f]{64}$/, 'Expected a SHA-256 hex digest');
const lwt = z.object({ l: z.number(), w: z.number(), t: z.number() });

export const PlacementSchema = z.object({ x: z.number(), y: z.number(), z: z.number(), sx: z.number(), sy: z.number(), sz: z.number() });
export type Placement = z.infer<typeof PlacementSchema>;

export const GlueUpSchema = z.object({ strips: z.int().min(2), stripWidthTicks: z.int().positive() });
export type GlueUp = z.infer<typeof GlueUpSchema>;

/** Roles are open-ended; these are the ones the v1 families use. */
export const KNOWN_ROLES = ['side', 'top', 'bottom', 'shelf', 'divider', 'back', 'plinth', 'end', 'post', 'block', 'slat', 'rail'] as const;

export const AssemblyPartSchema = z.object({
  id: z.string().min(1),
  role: z.string().min(1),
  label: z.string(),
  qty: z.int().positive(),
  material: z.string(),
  stockId: z.string().min(1),
  thicknessTicks: z.number(),
  /** Ticks; `l` is always along the grain. */
  finished: lwt,
  cut: lwt,
  grainAxis: z.literal('l'),
  grainWorld: z.enum(['x', 'y', 'z']),
  face: z.string(),
  edge: z.string(),
  /** Millimetres: minimum corner and extents. */
  placement: PlacementSchema,
  glueUp: GlueUpSchema.optional(),
  /** Horizontal parts: underside from the floor, mm. */
  datum: z.number().optional(),
  shelfNo: z.int().optional(),
  /** Multi-bay families: 0-based bay (column) index. Absent for a one-bay piece. */
  bay: z.int().optional(),
});
export type AssemblyPart = z.infer<typeof AssemblyPartSchema>;

/**
 * Joint kinds (kit v1.1.0 names): `mortise-tenon` joins a slat or a rail to its frame (FND-13);
 * `through-tenon` (a shelf tenoned right through a thick side) and `mitre` (a thick frame's
 * corners) are the chunky solid frame's joints (FND-23).
 */
export const JointKindSchema = z.enum(['housed-dado', 'dowel-butt', 'screwed-butt', 'rebate', 'pocket-screw', 'egg-crate', 'mortise-tenon', 'through-tenon', 'mitre', 'hinge', 'runner', 'track']);
export type JointKind = z.infer<typeof JointKindSchema>;

export const JointSchema = z.object({
  id: z.string().min(1),
  a: z.string().min(1),
  b: z.string().min(1),
  kind: JointKindSchema,
  /** Which face of `b` the joint is on ("inner", "back", "underside" …). */
  face: z.string(),
  /** Where along the joint line it sits (e.g. the shelf underside from the floor), mm. */
  datumMm: z.number(),
  visible: z.boolean(),
});
export type Joint = z.infer<typeof JointSchema>;

export const HardwareSchema = z.object({
  kind: z.string().min(1),
  qty: z.int().nonnegative(),
  at: z.array(z.object({ partId: z.string(), x: z.number(), y: z.number(), z: z.number() })),
  visible: z.boolean(),
  spec: z.string().optional(),
});
export type Hardware = z.infer<typeof HardwareSchema>;

export const BoundsSchema = z.object({ x0: z.number(), x1: z.number(), y0: z.number(), y1: z.number() });
export type Bounds = z.infer<typeof BoundsSchema>;

export const AssemblyOpeningSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  bounds: BoundsSchema,
  clearMm: z.number(),
});
export type AssemblyOpening = z.infer<typeof AssemblyOpeningSchema>;

export const HandleSchema = z.object({
  partId: z.string(),
  parameterKey: z.string(),
  axis: z.enum(['x', 'y']),
  /** Which entry of a list parameter the handle moves (e.g. the shelf's index), if any. */
  index: z.int().optional(),
  bay: z.int().optional(),
});
export type Handle = z.infer<typeof HandleSchema>;

export const StockFitSchema = z.object({ partId: z.string(), fits: z.boolean(), rotationUsed: z.boolean(), note: z.string() });
export type StockFit = z.infer<typeof StockFitSchema>;

/**
 * A one-click repair offered with an error (or a span warning). `patch` is flat: each key is
 * either a top-level design key (W, H, D in ticks, `openings`, `bayLayouts` …), set directly, or
 * a family parameter key (`tierEnds`, `horizontals`, `rowDividerXs`, `feetHeight` …), set through
 * the family's `setParam`. `applyFix` (src/families/fix.ts) applies it exactly as the UI does;
 * the patched design no longer has that issue (tests/families/fix.test.ts checks every kind).
 */
export const IssueFixSchema = z.object({ label: z.string().min(1), patch: z.record(z.string(), z.unknown()) });
export type IssueFix = z.infer<typeof IssueFixSchema>;
export const IssueSchema = z.object({ field: z.string(), message: z.string(), fix: IssueFixSchema.optional() });
export type Issue = z.infer<typeof IssueSchema>;
export const ValidationSchema = z.object({ ok: z.boolean(), errors: z.array(IssueSchema), warnings: z.array(IssueSchema) });
export type Validation = z.infer<typeof ValidationSchema>;

/** A mark the maker sets out from the floor (shelf undersides), mm. */
export const DatumSchema = z.object({ partId: z.string(), label: z.string(), mm: z.number(), bay: z.int().optional() });
export type Datum = z.infer<typeof DatumSchema>;

/** Per-stock summary for the pack (grouping by material). */
export const StockUseSchema = z.object({
  id: z.string(),
  version: z.string(),
  /** `order`: bought to size (glass), not cut from stock. */
  kind: z.enum(['sheet', 'board', 'order']),
  material: z.string(),
  label: z.string(),
  thicknessTicks: z.number(),
  /** Sheet stock: the sheet size (ticks). */
  sheet: z.object({ lengthTicks: z.number(), widthTicks: z.number() }).optional(),
  /** Board stock: the widest board sold (ticks). */
  maxWidthTicks: z.number().optional(),
  /** kg/m³ for the mass advisory. */
  density: z.number().optional(),
});
export type StockUse = z.infer<typeof StockUseSchema>;

export const AssemblyVersionsSchema = z.object({
  family: z.string(),
  stock: z.string(),
  joints: z.string(),
  generator: z.string(),
  /** The construction profile in its first-slice form (same as `joints`). */
  construction: z.string().optional(),
});

export const BoardAssemblySchema = z.object({
  schemaVersion: z.literal(1),
  familyId: z.string().min(1),
  familyVersion: z.string().min(1),
  familyLabel: z.string(),
  parts: z.array(AssemblyPartSchema),
  joints: z.array(JointSchema),
  hardware: z.array(HardwareSchema),
  openings: z.array(AssemblyOpeningSchema),
  handles: z.array(HandleSchema),
  datums: z.array(DatumSchema),
  /** mm; families may add their own limits. */
  envelope: z.looseObject({ W: z.number(), H: z.number(), D: z.number() }),
  stocks: z.array(StockUseSchema),
  stockFit: z.array(StockFitSchema),
  validation: ValidationSchema,
  versions: AssemblyVersionsSchema,
  manufacturingHash: hex64,
  appearanceHash: hex64,
  /** v2 contract (src/families/contract.ts): additive, outside `manufacturingHash`, with its own `assemblyHash`. */
  contract: ContractSchema.optional(),
});
export type BoardAssembly = z.infer<typeof BoardAssemblySchema>;
